import React, { useEffect } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { isEmpty } from '@peloton/helpers/isEmpty';
import { media } from '@peloton/styles';
import { backgroundGray } from '@engage/colors';
import {
  open as trackOpenAction,
  close as trackCloseAction,
  LibraryType,
  getAppliedFilters,
  ClosedBySource,
  getAppliedFilterCount,
} from '@engage/filters';
import {
  getBrowseCategories,
  requestNewClasses,
  getActiveCategorySlug,
  loadingFirstPage as loadingFirstPageSelector,
  getActiveCategoryName,
  requestMoreClasses,
} from '@engage/library';
import { useReduxState, useReduxAction } from '@engage/redux';
import { getOkValue, isPending } from '@engage/result';
import { Source } from '@engage/settings/analytics';
import { spaces } from '@engage/styles';
import type { BrowseCategorySlug } from '@members/browse-categories';
import { BROWSE_CATEGORY_HEIGHT } from '@members/browse-categories';
import { toConnectedBrowseCategory } from '@members/browse-categories/toConnectedBrowseCategory';
import { useClasses, MembersCopy } from '@members/copy';
import { Classes as ClassesCopy } from '@members/copy/models.generated/Classes';
import { Members } from '@members/copy/models.generated/Members';
import { SkipLinks } from '@members/copy/models.generated/SkipLinks';
import {
  LayoutWithoutUserNav,
  categoriesElementId,
  classSelectionElementId,
} from '@members/layout';
import { TOP_NAV_HEIGHT_PX } from '@members/layout/nav/containers';
import Classes from '@members/library/Classes/Classes';
import { Loading } from '@members/library/Classes/Loading';
import BookmarkToast from '@members/notifications/BookmarkToast';
import { BookmarkPages } from '@members/notifications/constants';
import { useBookmarkPage } from '@members/notifications/hooks/useBookmarkPage';
import { ConnectedOnDemandModal } from '@members/on-demand-modal/ConnectedOnDemandModal';
import { NoClassesBecauseLanguage } from '@members/settings';
import { QuotaInformationSection } from '@members/tiering/components/QuotaInformationSection';
import { useShouldShowQuotaOnLibraryPage } from '@members/tiering/hooks/useShouldShowQuotaOnLibraryPage';
import { FilterFlyout } from './Filters/FilterFlyout';
import { FilterRow } from './Filters/FilterRow';
import { MobileFilterButton } from './Filters/MobileFilterButton';
import { useSyncStateToUrl, useSyncUrlToState } from './hooks';
import { WidthContainer } from './shared';
import { toLibraryCategoryRoute } from './urls';
import { useHasClassesWithoutLanguagePreference } from './useHasClassesWithoutLanguagePreference';

type Props = RouteComponentProps<{ categorySlug: BrowseCategorySlug }>;

const LibraryPageView: React.FC<React.PropsWithChildren<Props>> = ({
  match: {
    params: { categorySlug },
  },
}) => {
  useLoadPage(categorySlug);
  const browseCategoryName = useReduxState(getActiveCategoryName);
  const loadMore = useReduxAction(requestMoreClasses);
  const titleCopy = useClasses(ClassesCopy.Title);
  const noFiltersApplied = isEmpty(useReduxState(getAppliedFilters));
  const appliedFilterCount = useReduxState(getAppliedFilterCount);
  const { isLoading, showLanguageMessage } = useLoadingState();
  const { active, toCloseHandler, open, toToggleHandler } = useOpenClose();
  useSyncStateToUrl();
  useSyncUrlToState(isLoading);
  const isBookmarkedPage = useBookmarkPage(BookmarkPages.LibraryPage);

  const shouldShowQuotaInformation = useShouldShowQuotaOnLibraryPage(categorySlug);

  return (
    <LayoutWithoutUserNav
      shouldShowFooter={false}
      titleSegments={browseCategoryName ? [browseCategoryName, titleCopy] : [titleCopy]}
      skipLinks={{
        first: {
          skipLinkId: SkipLinks.SkipToCategories,
          contentElementId: categoriesElementId,
        },
        second: {
          skipLinkId: SkipLinks.SkipToClassSelection,
          contentElementId: classSelectionElementId,
        },
      }}
    >
      <BookmarkToast
        pageName={BookmarkPages.LibraryPage}
        isBookmarkEnabled={isBookmarkedPage}
      >
        <MembersCopy id={Members.BookmarkPage} />
      </BookmarkToast>
      <CategorySelectionContainer id={categoriesElementId}>
        <BrowseCategory />
      </CategorySelectionContainer>

      {isLoading ? (
        <Loading isLoading={true} size={40} />
      ) : (
        <Main id={classSelectionElementId}>
          <FilterRowStickyContainer hidden={showLanguageMessage && noFiltersApplied}>
            <WidthContainer>
              <FilterRow toggleAction={toToggleHandler(ClosedBySource.Cancel)} />
            </WidthContainer>
          </FilterRowStickyContainer>
          {shouldShowQuotaInformation && (
            <WidthContainer>
              <QuotaInformationSection
                margin={`${spaces.xSmall}px auto ${spaces.medium}px;`}
                border=""
                source="Library Page"
              />
            </WidthContainer>
          )}
          {showLanguageMessage ? <LanguageMessage /> : <Classes loadMore={loadMore} />}
        </Main>
      )}

      <FilterFlyout active={active} toCloseHandler={toCloseHandler} open={open} />

      {showLanguageMessage ? null : (
        <MobileFilterButton
          toggle={toToggleHandler(ClosedBySource.Cancel)}
          appliedFilterCount={appliedFilterCount}
        />
      )}
      <ConnectedOnDemandModal overrideTabletStyleWithDesktop />
    </LayoutWithoutUserNav>
  );
};

const useLoadPage = (categorySlug: BrowseCategorySlug) => {
  const loadPage = useReduxAction(requestNewClasses);
  useEffect(() => {
    loadPage(categorySlug);
  }, [categorySlug]);
};

const useLoadingState = () => {
  const hasClassesInOtherLanguages = useHasClassesWithoutLanguagePreference();
  const loadingFirstPage = useReduxState(loadingFirstPageSelector);

  return {
    showLanguageMessage: getOkValue(hasClassesInOtherLanguages),
    isLoading: loadingFirstPage || isPending(hasClassesInOtherLanguages),
  };
};

const useOpenClose = () => {
  /**
   * allows filter to be opened by default on navigation
   * (like when navigating from the homepage onboarding checklist)
   */
  const history = useHistory<any>();
  const openFlyoutByDefault = history.location.state?.openFlyoutByDefault;

  const [active, setActive] = React.useState(openFlyoutByDefault);
  const trackClose = useReduxAction(trackCloseAction);
  const trackOpen = useReduxAction(trackOpenAction);

  const toCloseHandler = React.useCallback(
    (closedBySource: ClosedBySource) => () => {
      trackClose(LibraryType.OnDemandLibrary, closedBySource);
      setActive(false);
    },
    [setActive],
  );

  const open = React.useCallback(() => {
    trackOpen(LibraryType.OnDemandLibrary);
    setActive(true);
  }, [setActive]);

  const toToggleHandler = React.useCallback(
    (closedBySource: ClosedBySource) => () => {
      active ? toCloseHandler(closedBySource) : open();
    },
    [open, toCloseHandler],
  );

  return { toCloseHandler, active, open, toToggleHandler };
};

const BrowseCategory = toConnectedBrowseCategory(
  getBrowseCategories,
  toLibraryCategoryRoute,
);

const LanguageMessage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const onSave = useReduxAction(requestNewClasses);
  const slug = useReduxState(getActiveCategorySlug);

  return (
    <NoClassesBecauseLanguage
      onSave={() => onSave(slug)}
      source={Source.LibraryLimitedResults}
    />
  );
};

const Main = styled.main`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: 450px;
`;

const FilterRowStickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${backgroundGray};

  ${media.tabletXLarge`
    top: ${BROWSE_CATEGORY_HEIGHT + TOP_NAV_HEIGHT_PX}px;
  `}
`;

const CategorySelectionContainer = styled.div`
  ${media.tabletXLarge`
    position: sticky;
    top: ${TOP_NAV_HEIGHT_PX}px;
    z-index: 2;
  `}
`;

export default LibraryPageView;
